import { useState, useEffect } from 'react';
import {
  Card,
  Heading,
  Text,
  Flex,
  Button,
  IconButton,
  Icon,
  Spacer,
  Container,
  Badge,
  Table,
  Box,
  Stack,
  Input,
  HStack,
  FileUpload,
  Dialog,
  CloseButton,
  Portal,
} from '@chakra-ui/react';
import { useParams, useLoaderData, useNavigate } from 'react-router-dom';
import { Document } from '@/lib/types';
import { SparklesIcon, TrashIcon, Squares2X2Icon, TagIcon, ArrowUpTrayIcon, CloudArrowUpIcon, ServerIcon } from '@heroicons/react/24/outline';
import QuickSearchForm from '@/components/ui/quick-search-form';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import { useAuth } from '@/lib/hooks/use-auth';
import { getFilenameFromPath } from '@/lib/helpers';
import getSportName from '@/utils/get-sport-name';
import api from '@/utils/api';
import { toaster } from '@/components/ui/toaster';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import { Prose } from '@/components/ui/prose';
import { AccordionRoot, AccordionItem, AccordionItemTrigger, AccordionItemContent } from '@/components/ui/accordion';
import { Tooltip } from '@/components/ui/tooltip';

interface Props {
  document: Document;
  isCooldownActive: boolean;
  cooldownTimeLeft: number;
  callIngest: (fileKey: string) => Promise<void>;
  onDelete: (doc: Document) => void;
  canDelete: boolean;
  onViewChunks: (doc: Document) => void;
  onViewContentNames: (doc: Document) => void;
  onPineconeUpload: (doc: Document) => Promise<void>;
  onPineconeDelete: (doc: Document) => Promise<void>;
  pineconeStatus: { [key: string]: 'uploading' | 'deleting' | 'checking' | 'exists' | 'not_exists' | 'error' };
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const TableRow = ({ document, isCooldownActive, cooldownTimeLeft, callIngest, onDelete, canDelete, onViewChunks, onViewContentNames, onPineconeUpload, onPineconeDelete, pineconeStatus }: Props) => {
  return (
    <Table.Row>
      <Table.Cell>
        <Text 
          color="fg" 
          fontSize={'sm'} 
          fontWeight={'medium'}
          title={document.key}
          cursor="default"
          maxW="350px"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {getFilenameFromPath(document.key)}
        </Text>
        {document.vectorized && (
          <Badge variant='surface' size='xs' colorPalette='green'>
            Vectorized
          </Badge>
        )}
      </Table.Cell>
      <Table.Cell>
        <Text color="fg.muted" fontSize={'sm'}>
          {formatFileSize(document.size)}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <Text color="fg.subtle" fontSize={'sm'}>
          {formatDate(document.last_modified)}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <Flex gap={2} align="center">
          {!document.vectorized && (
            <Tooltip content={isCooldownActive ? `Cooldown: ${Math.floor(cooldownTimeLeft / 60)}:${(cooldownTimeLeft % 60).toString().padStart(2, '0')}` : 'Use AI'}>
              <IconButton
                aria-label="Use AI"
                colorPalette='brand'
                variant='ghost'
                size='xs'
                onClick={() => callIngest(document.key)}
                disabled={isCooldownActive}
              >
                <Icon as={SparklesIcon} w={4} h={4} />
              </IconButton>
            </Tooltip>
          )}
          {document.vectorized && (
            <Tooltip content={'View Chunks'}>
              <IconButton
                aria-label="View Chunks"
                variant='ghost'
                size='xs'
                onClick={() => onViewChunks(document)}
              >
                <Icon as={Squares2X2Icon} w={4} h={4} />
              </IconButton>
            </Tooltip>
          )}
          {document.vectorized && (
            <Tooltip content={'Content Names'}>
              <IconButton
                aria-label="Content Names"
                variant='ghost'
                size='xs'
                onClick={() => onViewContentNames(document)}
              >
                <Icon as={TagIcon} w={4} h={4} />
              </IconButton>
            </Tooltip>
          )}
          {document.vectorized && (
            <Tooltip content={
              pineconeStatus[document.key] === 'checking' ? 'Checking chat support status...' :
              pineconeStatus[document.key] === 'exists' ? 'Remove from chat support knowledge' : 
              pineconeStatus[document.key] === 'error' ? 'Error checking status, click to retry' :
              'Upload for chat support knowledge'
            }>
              <IconButton
                aria-label={pineconeStatus[document.key] === 'exists' ? 'Remove from chat support knowledge' : 'Upload for chat support knowledge'}
                colorPalette={pineconeStatus[document.key] === 'exists' ? 'red' : 'blue'}
                variant='ghost'
                size='xs'
                onClick={() => pineconeStatus[document.key] === 'exists' ? onPineconeDelete(document) : onPineconeUpload(document)}
                loading={pineconeStatus[document.key] === 'uploading' || pineconeStatus[document.key] === 'deleting' || pineconeStatus[document.key] === 'checking'}
                disabled={pineconeStatus[document.key] === 'checking'}
              >
                <Icon as={pineconeStatus[document.key] === 'exists' ? ServerIcon : CloudArrowUpIcon} w={4} h={4} />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip content={'Delete'}>
              <IconButton
                aria-label="Delete"
                variant='ghost'
                size='xs'
                onClick={() => onDelete(document)}
              >
                <Icon as={TrashIcon} w={4} h={4} />
              </IconButton>
            </Tooltip>
          )}
        </Flex>
      </Table.Cell>
    </Table.Row>
  );
};

const MobileDocumentCard = ({ document, isCooldownActive, cooldownTimeLeft, callIngest, onDelete, canDelete, onViewChunks, onViewContentNames, onPineconeUpload, onPineconeDelete, pineconeStatus }: Props) => {
  return (
    <Card.Root>
      <Card.Body p={4}>
        <Stack gap={3}>
          <Flex justify="space-between" align="flex-start" gap={2}>
            <Text 
              color="fg" 
              fontSize="md" 
              fontWeight="medium"
              flex={1}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              title={document.key}
            >
              {getFilenameFromPath(document.key)}
            </Text>
            {document.vectorized && (
              <Badge variant='surface' size='sm' colorPalette='green'>
                Vectorized
              </Badge>
            )}
          </Flex>

          <Flex justify="space-between" align="center" gap={2}>
            <Text color="fg.muted" fontSize="sm">
              {formatFileSize(document.size)}
            </Text>
            <Text color="fg.subtle" fontSize="sm">
              {formatDate(document.last_modified)}
            </Text>
          </Flex>

          <Flex gap={3} wrap="wrap">
            {!document.vectorized && (
              <Tooltip content={isCooldownActive ? `Cooldown: ${Math.floor(cooldownTimeLeft / 60)}:${(cooldownTimeLeft % 60).toString().padStart(2, '0')}` : 'Use AI'}>
                <IconButton
                  aria-label="Use AI"
                  colorPalette='brand'
                  variant='ghost'
                  size='sm'
                  onClick={() => callIngest(document.key)}
                  disabled={isCooldownActive}
                >
                  <Icon as={SparklesIcon} w={5} h={5} />
                </IconButton>
              </Tooltip>
            )}
            {document.vectorized && (
              <Tooltip content={'View Chunks'}>
                <IconButton
                  aria-label="View Chunks"
                  variant='ghost'
                  size='sm'
                  onClick={() => onViewChunks(document)}
                >
                  <Icon as={Squares2X2Icon} w={5} h={5} />
                </IconButton>
              </Tooltip>
            )}
            {document.vectorized && (
              <Tooltip content={'Content Names'}>
                <IconButton
                  aria-label="Content Names"
                  variant='ghost'
                  size='sm'
                  onClick={() => onViewContentNames(document)}
                >
                  <Icon as={TagIcon} w={5} h={5} />
                </IconButton>
              </Tooltip>
            )}
            {document.vectorized && (
              <Tooltip content={
                pineconeStatus[document.key] === 'checking' ? 'Checking chat support status...' :
                pineconeStatus[document.key] === 'exists' ? 'Remove from chat support knowledge' : 
                pineconeStatus[document.key] === 'error' ? 'Error checking status, click to retry' :
                'Upload for chat support knowledge'
              }>
                <IconButton
                  aria-label={pineconeStatus[document.key] === 'exists' ? 'Remove from chat support knowledge' : 'Upload for chat support knowledge'}
                  colorPalette={pineconeStatus[document.key] === 'exists' ? 'red' : 'blue'}
                  variant='ghost'
                  size='sm'
                  onClick={() => pineconeStatus[document.key] === 'exists' ? onPineconeDelete(document) : onPineconeUpload(document)}
                  loading={pineconeStatus[document.key] === 'uploading' || pineconeStatus[document.key] === 'deleting' || pineconeStatus[document.key] === 'checking'}
                  disabled={pineconeStatus[document.key] === 'checking'}
                >
                  <Icon as={pineconeStatus[document.key] === 'exists' ? ServerIcon : CloudArrowUpIcon} w={5} h={5} />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip content={'Delete'}>
                <IconButton
                  aria-label="Delete"
                  variant='ghost'
                  size='sm'
                  onClick={() => onDelete(document)}
                >
                  <Icon as={TrashIcon} w={5} h={5} />
                </IconButton>
              </Tooltip>
            )}
          </Flex>
        </Stack>
      </Card.Body>
    </Card.Root>
  );
};

export default function Documents() {
  const isAdmin = useIsInAdminGroup();
  const isCancelled = useIsCancelled();
  const documents = useLoaderData() as Document[];
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [searchResults, setSearchResults] = useState<Document[]>([]);
  const navigate = useNavigate();
  const { workspaceId, contentTypeId } = useParams();
  const { workspace } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [showChunksModal, setShowChunksModal] = useState(false);
  const [isLoadingChunks, setIsLoadingChunks] = useState(false);
  const [chunksDocument, setChunksDocument] = useState<Document | null>(null);
  const [chunks, setChunks] = useState<Array<{ 
    id?: number; 
    text: string; 
    metadata?: string;
    heading_level?: number;
    parent_section_id?: number | null;
    section_title?: string;
    token_count?: number;
    section_path?: string;
    uuid?: string;
  }>>([]);
  const [showNamesModal, setShowNamesModal] = useState(false);
  const [isLoadingNames, setIsLoadingNames] = useState(false);
  const [namesDocument, setNamesDocument] = useState<Document | null>(null);
  const [contentNames, setContentNames] = useState<Array<{ content_type: string; name: string; description?: string }>>([]);
  const [filteredContentNames, setFilteredContentNames] = useState<Array<{ content_type: string; name: string; description?: string }>>([]);
  const [namesSearch, setNamesSearch] = useState('');
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  
  const [isCooldownActive, setIsCooldownActive] = useState(false);
  const [cooldownTimeLeft, setCooldownTimeLeft] = useState(0);
  const [cooldownInterval, setCooldownInterval] = useState<number | null>(null);
  const [pineconeStatus, setPineconeStatus] = useState<{ [key: string]: 'uploading' | 'deleting' | 'checking' | 'exists' | 'not_exists' | 'error' }>({});

  const COOLDOWN_STORAGE_KEY = `document-cooldown-${workspaceId}`;

  // Chat widget visibility is controlled by GlobalChatWidget component based on Pinecone files

  useEffect(() => {
    const savedCooldown = localStorage.getItem(COOLDOWN_STORAGE_KEY);
    if (savedCooldown) {
      try {
        const { endTime } = JSON.parse(savedCooldown);
        const now = Date.now();
        const timeLeft = Math.max(0, Math.ceil((endTime - now) / 1000));
        
        if (timeLeft > 0) {
          setIsCooldownActive(true);
          setCooldownTimeLeft(timeLeft);
          
          // Start countdown from saved time
          const interval = setInterval(() => {
            setCooldownTimeLeft((prev) => {
              if (prev <= 1) {
                clearInterval(interval);
                setIsCooldownActive(false);
                setCooldownInterval(null);
                localStorage.removeItem(COOLDOWN_STORAGE_KEY);
                
                window.location.reload();

                return 0;
              }
              return prev - 1;
            });
          }, 1000);
          
          setCooldownInterval(interval);
        } else {
          // Cooldown expired, clean up
          localStorage.removeItem(COOLDOWN_STORAGE_KEY);
        }
      } catch (error) {
        console.error('Error getting cooldown:', error);
        localStorage.removeItem(COOLDOWN_STORAGE_KEY);
      }
    }
  }, [COOLDOWN_STORAGE_KEY]);

  useEffect(() => {
    // Sort documents by last_modified in descending order (newest first)
    const sortedDocuments = [...documents].sort((a, b) => {
      const aTime = new Date(a.last_modified).getTime();
      const bTime = new Date(b.last_modified).getTime();
      return bTime - aTime; // Descending order
    });
    
    setFilteredDocuments(sortedDocuments);
    setSearchResults(sortedDocuments);
  }, [documents]);

  useEffect(() => {
    // Check Pinecone status for vectorized documents when they change
    const vectorizedDocs = searchResults.filter(doc => doc.vectorized);
    vectorizedDocs.forEach(doc => {
      if (!pineconeStatus[doc.key]) {
        checkPineconeStatus(doc);
      }
    });
  }, [searchResults]);

  useEffect(() => {
    return () => {
      if (cooldownInterval) {
        clearInterval(cooldownInterval);
      }
    };
  }, [cooldownInterval]);

  const startCooldown = () => {
    setIsCooldownActive(true);
    setCooldownTimeLeft(120); // 2 minutes in seconds
    
    // Save cooldown end time to localStorage
    const endTime = Date.now() + (120 * 1000); // 2 minutes from now
    localStorage.setItem(COOLDOWN_STORAGE_KEY, JSON.stringify({ endTime }));
    
    const interval = setInterval(() => {
      setCooldownTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsCooldownActive(false);
          setCooldownInterval(null);
          localStorage.removeItem(COOLDOWN_STORAGE_KEY);
          
          window.location.reload();
          
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    setCooldownInterval(interval);
  };

  const callIngest = async (fileKey: string) => {
    if (!workspace || !contentTypeId) {
      return;
    }

    try {
      const body = {
        "country": workspace.country || 'usa',
        "s3_uri": fileKey,
        "sport_type": getSportName(workspace.sport_type_id),
        "workspace_id": workspace.id,
        "content_type_id": parseInt(contentTypeId, 10),
        "upload_to_pinecone": true,
        "assistant_name": 'genai',
      }

      startCooldown();

      const response = await api.post('/documents/ingest', body);

      if (response.data.error) {
        toaster.create({
          title: response.data.error,
          type: 'success',
        });
      } else if (response.data.resource) {
        toaster.create({
          title: 'Document ingested successfully',
          type: 'success',
        });
      }

      navigate('.', { replace: true });
    } catch (error) {
      console.error('Error calling generate content API:', error);
      navigate('.', { replace: true });
    }
  };

  const checkPineconeStatus = async (doc: Document) => {
    if (!workspace || !contentTypeId) {
      return;
    }

    setPineconeStatus(prev => ({ ...prev, [doc.key]: 'checking' }));

    try {
      const response = await api.post('/pinecone/files/check', {
        workspace_id: workspace.id,
        content_type_id: parseInt(contentTypeId, 10),
        sport_type: getSportName(workspace.sport_type_id),
        country: workspace.country || 'usa',
        s3_uri: doc.key,
      });

      // Handle different response formats
      const exists = response.data?.exists === true || 
                     response.data?.data?.exists === true ||
                     response.data?.status === 'exists';

      setPineconeStatus(prev => ({ 
        ...prev, 
        [doc.key]: exists ? 'exists' : 'not_exists' 
      }));
    } catch (error) {
      console.error('Error checking Pinecone status:', error);
      setPineconeStatus(prev => ({ ...prev, [doc.key]: 'error' }));
    }
  };

  const handlePineconeUpload = async (doc: Document) => {
    if (!workspace || !contentTypeId) {
      return;
    }

    // Check if already exists before uploading
    const currentStatus = pineconeStatus[doc.key];
    if (currentStatus === 'exists') {
      toaster.create({
        title: 'Document already uploaded for chat support',
        type: 'info',
      });
      return;
    }

    if (currentStatus === 'checking' || currentStatus === 'uploading') {
      return;
    }

    setPineconeStatus(prev => ({ ...prev, [doc.key]: 'uploading' }));

    try {
      await api.post('/pinecone/files/upload', {
        workspace_id: workspace.id,
        content_type_id: parseInt(contentTypeId, 10),
        s3_uri: doc.key,
        sport_type: getSportName(workspace.sport_type_id),
        country: workspace.country || 'usa',
      });

      setPineconeStatus(prev => ({ ...prev, [doc.key]: 'exists' }));
      toaster.create({
        title: 'Document uploaded for chat support successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Error uploading to Pinecone:', error);
      setPineconeStatus(prev => ({ ...prev, [doc.key]: 'error' }));
      toaster.create({
        title: 'Failed to upload document for chat support',
        type: 'error',
      });
    }
  };

  const handlePineconeDelete = async (doc: Document) => {
    if (!workspace || !contentTypeId) {
      return;
    }

    setPineconeStatus(prev => ({ ...prev, [doc.key]: 'deleting' }));

    try {
      await api.delete('/pinecone/files', {
        data: {
          workspace_id: workspace.id,
          content_type_id: parseInt(contentTypeId, 10),
          s3_key: doc.key,
        },
      });

      setPineconeStatus(prev => ({ ...prev, [doc.key]: 'not_exists' }));
      toaster.create({
        title: 'Document removed from chat support successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Error deleting from Pinecone:', error);
      setPineconeStatus(prev => ({ ...prev, [doc.key]: 'error' }));
      toaster.create({
        title: 'Failed to remove document from chat support',
        type: 'error',
      });
    }
  };

  const handleDelete = (doc: Document) => {
    setDocumentToDelete(doc);
    setShowDeleteModal(true);
  };

  const onConfirmDelete = async () => {
    if (!workspaceId || !documentToDelete) {
      return;
    }
    setIsDeleting(true);
    try {
      // Delete from S3/database
      await api.delete(`documents/${workspaceId}/${contentTypeId}`, {
        data: { s3_key: documentToDelete.key },
      });

      // Also delete from Pinecone if it exists there
      if (workspace && contentTypeId && pineconeStatus[documentToDelete.key] === 'exists') {
        try {
          await api.delete('/pinecone/files', {
            data: {
              workspace_id: workspace.id,
              content_type_id: parseInt(contentTypeId, 10),
              s3_key: documentToDelete.key,
            },
          });
        } catch (pineconeError) {
          console.error('Error removing from Pinecone:', pineconeError);
        }
      }

      setFilteredDocuments((prev) => prev.filter((d) => d.key !== documentToDelete.key));
      setSearchResults((prev) => prev.filter((d) => d.key !== documentToDelete.key));
      setPineconeStatus(prev => {
        const newStatus = { ...prev };
        delete newStatus[documentToDelete.key];
        return newStatus;
      });

      toaster.create({ title: 'Document deleted', type: 'success' });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err?.response?.data?.error || 'Failed to delete document',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDocumentToDelete(null);
    }
  };

  const handleViewChunks = async (doc: Document) => {
    if (!workspaceId) {
      return;
    }
    setChunksDocument(doc);
    setShowChunksModal(true);
    setIsLoadingChunks(true);
    try {
      const response = await api.get('/documents/vectors-sections', {
        params: {
          workspace_id: workspaceId,
          filename: doc.key,
        },
      });
      const data = response?.data?.data || [];
      setChunks(data);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err?.response?.data?.error || 'Failed to fetch document sections',
      });
    } finally {
      setIsLoadingChunks(false);
    }
  };

  const handleViewContentNames = async (doc: Document) => {
    if (!workspaceId || !contentTypeId) {
      return;
    }
    setNamesDocument(doc);
    setShowNamesModal(true);
    setIsLoadingNames(true);
    try {
      const response = await api.get(`/content-names/workspace/${workspaceId}/${contentTypeId}`, {
        params: {
          s3_key: doc.key,
        },
      });
      const data = response?.data?.data || [];
      setContentNames(data);
      setFilteredContentNames(data);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err?.response?.data?.error || 'Failed to fetch content names',
      });
    } finally {
      setIsLoadingNames(false);
    }
  };

  const handleUploadAccept = (details: any) => {
    if (details && details.files.length > 0) {
      setUploadFiles((prev) => [...prev, ...details.files]);
    }
  };

  const handleUploadSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!workspaceId || !contentTypeId || uploadFiles.length === 0) return;

    try {
      setIsUploading(true);
      const formData = new FormData();
      uploadFiles.forEach((f) => formData.append('file', f));

      await api.post(`documents/${workspaceId}/${contentTypeId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toaster.create({ title: 'Documents uploaded successfully', type: 'success' });
      setUploadFiles([]);
      navigate('.', { replace: true });
    } catch (err: any) {
      toaster.create({ type: 'error', title: err?.response?.data?.error || 'Failed to upload documents' });
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (!namesSearch) {
      setFilteredContentNames(contentNames);
      return;
    }
    const q = namesSearch.toLowerCase();
    const filtered = contentNames.filter((item) =>
      (item.content_type || '').toLowerCase().includes(q) || 
      (item.name || '').toLowerCase().includes(q) || 
      (item.description || '').toLowerCase().includes(q),
    );
    setFilteredContentNames(filtered);
  }, [namesSearch, contentNames]);

  const handleSearch = (search: string) => {
    const filtered = filteredDocuments.filter((document) => {
      const filename = getFilenameFromPath(document.key);
      return (
        (document.key && document.key.toLowerCase().includes(search.toLowerCase())) ||
        (filename && filename.toLowerCase().includes(search.toLowerCase()))
      );
    });
    
    // Maintain sort order by last_modified (newest first)
    const sorted = [...filtered].sort((a, b) => {
      const aTime = new Date(a.last_modified).getTime();
      const bTime = new Date(b.last_modified).getTime();
      return bTime - aTime; // Descending order
    });
    
    setSearchResults(sorted);
  };

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header>
          <Container maxW={'4xl'}>
            <Stack gap={4} mb={4}>
              <Flex 
                direction={{ base: 'column', md: 'row' }} 
                align={{ base: 'stretch', md: 'center' }} 
                gap={4}
              >
                <Heading size={{ base: '2xl', md: '3xl' }}>Knowledge Base</Heading>
                <Spacer />
                {isAdmin && !isCancelled && (
                  <Button 
                    variant='outline'
                    onClick={() => navigate(`/${workspaceId}/content-types`)}
                    size={{ base: 'md', md: 'sm' }}
                    width={{ base: 'full', md: 'auto' }}
                  >
                    <Icon as={Squares2X2Icon} w={4} h={4} />
                    <Text display={{ base: 'none', sm: 'inline' }}>Content libraries</Text>
                    <Text display={{ base: 'inline', sm: 'none' }}>Libraries</Text>
                  </Button>
                )}
              </Flex>
            </Stack>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW={'4xl'}>
            {isAdmin && !isCancelled && (
              <Box mb={6}>
                <form onSubmit={handleUploadSubmit}>
                  <Stack gap={4}>
                    <Text fontSize='lg' color={'fg'} fontWeight={'semibold'}>
                      Upload documents
                    </Text>
                    <FileUpload.Root maxW='full' alignItems='stretch' maxFileSize={10 * 1024 * 1024} onFileAccept={handleUploadAccept}>
                      <FileUpload.HiddenInput />
                      <FileUpload.Dropzone minHeight='120px'>
                        <Icon as={ArrowUpTrayIcon} w={4} h={4} />
                        <FileUpload.DropzoneContent>
                          <Box>Drag and drop files here</Box>
                          <Box color='fg.muted'>All file types up to 10MB each</Box>
                        </FileUpload.DropzoneContent>
                      </FileUpload.Dropzone>
                      <FileUpload.List />
                    </FileUpload.Root>
                    <HStack>
                      <Button type='submit' colorPalette='brand' loading={isUploading} disabled={uploadFiles.length === 0}>
                        Upload
                      </Button>
                      <Button variant='ghost' onClick={() => setUploadFiles([])} disabled={uploadFiles.length === 0}>
                        Clear
                      </Button>
                    </HStack>
                  </Stack>
                </form>
              </Box>
            )}
            <Box mb={8}>
              <QuickSearchForm handleSearch={handleSearch} />
            </Box>

            <Box display={{ base: 'none', md: 'block' }}>
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Document Name</Table.ColumnHeader>
                    <Table.ColumnHeader>File Size</Table.ColumnHeader>
                    <Table.ColumnHeader>Last Modified</Table.ColumnHeader>
                    <Table.ColumnHeader>Actions</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {searchResults.map((document, index) => (
                    <TableRow 
                      key={index} 
                      document={document} 
                      isCooldownActive={isCooldownActive}
                      cooldownTimeLeft={cooldownTimeLeft}
                      callIngest={callIngest}
                      onDelete={handleDelete}
                      canDelete={isAdmin && !isCancelled}
                      onViewChunks={handleViewChunks}
                      onViewContentNames={handleViewContentNames}
                      onPineconeUpload={handlePineconeUpload}
                      onPineconeDelete={handlePineconeDelete}
                      pineconeStatus={pineconeStatus}
                    />
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>

            <Box display={{ base: 'block', md: 'none' }}>
              <Stack gap={3}>
                {searchResults.map((document, index) => (
                  <MobileDocumentCard 
                    key={index} 
                    document={document} 
                    isCooldownActive={isCooldownActive}
                    cooldownTimeLeft={cooldownTimeLeft}
                    callIngest={callIngest}
                    onDelete={handleDelete}
                    canDelete={isAdmin && !isCancelled}
                    onViewChunks={handleViewChunks}
                    onViewContentNames={handleViewContentNames}
                    onPineconeUpload={handlePineconeUpload}
                    onPineconeDelete={handlePineconeDelete}
                    pineconeStatus={pineconeStatus}
                  />
                ))}
              </Stack>
            </Box>
          </Container>
        </Card.Body>
      </Card.Root>

      {/* Keep mounted; unmounting an open Chakra dialog leaves the body pointer/scroll lock behind. */}
      <DestructiveModal
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={onConfirmDelete}
        isLoading={isDeleting}
        title={'Delete Document'}
        confirmText={'Confirm'}
      >
        {`You are about to delete "${documentToDelete ? getFilenameFromPath(documentToDelete.key) : ''}". Press 'Confirm' to continue.`}
      </DestructiveModal>

      {/* lazyMount/unmountOnExit (not conditional rendering) keep the DOM clean
          while letting the dialog finish its close transition — unmounting it
          open leaves the body pointer/scroll lock behind. */}
      <Dialog.Root
        lazyMount
        unmountOnExit
        open={showChunksModal}
        onOpenChange={() => setShowChunksModal(false)}
      >
          <Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content maxW={{ base: 'full', md: '3xl' }}>
                <Dialog.Header>
                  <Dialog.Title>
                    {`Document Sections for ${chunksDocument ? getFilenameFromPath(chunksDocument.key) : ''}`}
                  </Dialog.Title>
                </Dialog.Header>
                <Dialog.Body>
                  {isLoadingChunks ? (
                    <Text color="fg.muted">Loading...</Text>
                  ) : chunks.length === 0 ? (
                    <Text color="fg.muted">No sections found.</Text>
                  ) : (
                    <Box maxH={{ base: '60vh', md: '70vh' }} overflowY="auto">
                      <AccordionRoot collapsible multiple>
                        {chunks.map((chunk, idx) => {
                          let meta: any = null;
                          try {
                            meta = chunk.metadata ? JSON.parse(chunk.metadata as any) : null;
                          } catch {
                            meta = null;
                          }
                          const from = meta?.loc?.lines?.from;
                          const to = meta?.loc?.lines?.to;
                          return (
                            <AccordionItem key={chunk.id ?? idx} value={(chunk.id ?? idx).toString()}>
                              <AccordionItemTrigger>
                                <Flex align="center" justify="space-between" width="full" gap={2}>
                                  <Flex direction="column" align="start" flex={1}>
                                    <Text fontSize="sm" fontWeight="medium">
                                      {chunk.section_title || `Section ${idx + 1}`}
                                    </Text>
                                    {chunk.section_path && (
                                      <Text fontSize="xs" color="fg.muted">
                                        {chunk.section_path}
                                      </Text>
                                    )}
                                  </Flex>
                                  <Flex direction="column" align="end" gap={1}>
                                    {chunk.heading_level !== undefined && (
                                      <Badge size="xs" colorPalette="blue">
                                        H{chunk.heading_level}
                                      </Badge>
                                    )}
                                    {chunk.token_count !== undefined && (
                                      <Text fontSize="xs" color="fg.muted">
                                        {chunk.token_count} tokens
                                      </Text>
                                    )}
                                  </Flex>
                                </Flex>
                              </AccordionItemTrigger>
                              <AccordionItemContent>
                                <Stack gap={4} p={2}>
                                  {(meta || chunk.section_title || chunk.uuid) && (
                                    <Box>
                                      <Text fontSize="sm" color="fg.muted" mb={2}>Metadata</Text>
                                      <Table.Root size="sm">
                                        <Table.Body>
                                          {chunk.uuid && (
                                            <Table.Row>
                                              <Table.Cell width="160px"><Text fontSize="xs" color="fg.muted">UUID</Text></Table.Cell>
                                              <Table.Cell>
                                                <Text fontSize="xs" fontFamily="mono" overflowWrap="anywhere" wordBreak="break-word">
                                                  {chunk.uuid}
                                                </Text>
                                              </Table.Cell>
                                            </Table.Row>
                                          )}
                                          {chunk.section_title && (
                                            <Table.Row>
                                              <Table.Cell width="160px"><Text fontSize="xs" color="fg.muted">Section Title</Text></Table.Cell>
                                              <Table.Cell><Text fontSize="sm">{chunk.section_title}</Text></Table.Cell>
                                            </Table.Row>
                                          )}
                                          {chunk.section_path && (
                                            <Table.Row>
                                              <Table.Cell width="160px"><Text fontSize="xs" color="fg.muted">Section Path</Text></Table.Cell>
                                              <Table.Cell><Text fontSize="sm">{chunk.section_path}</Text></Table.Cell>
                                            </Table.Row>
                                          )}
                                          {chunk.heading_level !== undefined && (
                                            <Table.Row>
                                              <Table.Cell width="160px"><Text fontSize="xs" color="fg.muted">Heading Level</Text></Table.Cell>
                                              <Table.Cell><Text fontSize="sm">{chunk.heading_level}</Text></Table.Cell>
                                            </Table.Row>
                                          )}
                                          {chunk.parent_section_id !== undefined && chunk.parent_section_id !== null && (
                                            <Table.Row>
                                              <Table.Cell width="160px"><Text fontSize="xs" color="fg.muted">Parent Section ID</Text></Table.Cell>
                                              <Table.Cell><Text fontSize="sm">{chunk.parent_section_id}</Text></Table.Cell>
                                            </Table.Row>
                                          )}
                                          {chunk.token_count !== undefined && (
                                            <Table.Row>
                                              <Table.Cell width="160px"><Text fontSize="xs" color="fg.muted">Token Count</Text></Table.Cell>
                                              <Table.Cell><Text fontSize="sm">{chunk.token_count}</Text></Table.Cell>
                                            </Table.Row>
                                          )}
                                          {meta?.source && (
                                            <Table.Row>
                                              <Table.Cell width="160px"><Text fontSize="xs" color="fg.muted">Source</Text></Table.Cell>
                                              <Table.Cell><Text fontSize="sm">{meta.source}</Text></Table.Cell>
                                            </Table.Row>
                                          )}
                                          {meta?.filename && (
                                            <Table.Row>
                                              <Table.Cell width="160px"><Text fontSize="xs" color="fg.muted">Filename</Text></Table.Cell>
                                              <Table.Cell>
                                                <Text fontSize="sm" overflowWrap="anywhere" wordBreak="break-word">
                                                  {meta.filename}
                                                </Text>
                                              </Table.Cell>
                                            </Table.Row>
                                          )}
                                          {typeof meta?.workspace_id !== 'undefined' && (
                                            <Table.Row>
                                              <Table.Cell width="160px"><Text fontSize="xs" color="fg.muted">Workspace ID</Text></Table.Cell>
                                              <Table.Cell><Text fontSize="sm">{meta.workspace_id}</Text></Table.Cell>
                                            </Table.Row>
                                          )}
                                          {meta?.country && (
                                            <Table.Row>
                                              <Table.Cell width="160px"><Text fontSize="xs" color="fg.muted">Country</Text></Table.Cell>
                                              <Table.Cell><Text fontSize="sm">{meta.country}</Text></Table.Cell>
                                            </Table.Row>
                                          )}
                                          {meta?.sport_type && (
                                            <Table.Row>
                                              <Table.Cell width="160px"><Text fontSize="xs" color="fg.muted">Sport Type</Text></Table.Cell>
                                              <Table.Cell><Text fontSize="sm">{meta.sport_type}</Text></Table.Cell>
                                            </Table.Row>
                                          )}
                                          {meta?.blobType && (
                                            <Table.Row>
                                              <Table.Cell width="160px"><Text fontSize="xs" color="fg.muted">Blob Type</Text></Table.Cell>
                                              <Table.Cell><Text fontSize="sm">{meta.blobType}</Text></Table.Cell>
                                            </Table.Row>
                                          )}
                                          {(from || to) && (
                                            <Table.Row>
                                              <Table.Cell width="160px"><Text fontSize="xs" color="fg.muted">Line Range</Text></Table.Cell>
                                              <Table.Cell><Text fontSize="sm">{`${from ?? '?'}-${to ?? '?'}`}</Text></Table.Cell>
                                            </Table.Row>
                                          )}
                                        </Table.Body>
                                      </Table.Root>
                                    </Box>
                                  )}
                                  <Box>
                                    <Text fontSize="sm" color="fg.muted" mb={2}>Content</Text>
                                    <Prose>
                                      <Box as="pre" whiteSpace="pre-wrap" overflowWrap="anywhere" wordBreak="break-word">
                                        {chunk.text}
                                      </Box>
                                    </Prose>
                                  </Box>
                                </Stack>
                              </AccordionItemContent>
                            </AccordionItem>
                          );
                        })}
                      </AccordionRoot>
                    </Box>
                  )}
                </Dialog.Body>
                <Dialog.Footer>
                  <Button variant="ghost" onClick={() => setShowChunksModal(false)}>Close</Button>
                </Dialog.Footer>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>

      <Dialog.Root
        lazyMount
        unmountOnExit
        open={showNamesModal}
        onOpenChange={() => setShowNamesModal(false)}
      >
          <Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content maxW={{ base: 'full', md: '2xl' }}>
                <Dialog.Header>
                  <Dialog.Title>
                    {`Content Names for ${namesDocument ? getFilenameFromPath(namesDocument.key) : ''}`}
                  </Dialog.Title>
                </Dialog.Header>
                <Dialog.Body>
                  {isLoadingNames ? (
                    <Text color="fg.muted">Loading...</Text>
                  ) : contentNames.length === 0 ? (
                    <Text color="fg.muted">No content names found.</Text>
                  ) : (
                    <Stack gap={3}>
                      <Input
                        size="sm"
                        placeholder="Search by type, name or description"
                        value={namesSearch}
                        onChange={(e) => setNamesSearch(e.target.value)}
                      />
                      <Box maxH={{ base: '60vh', md: '70vh' }} overflowY="auto">
                        <Table.Root size="sm">
                          <Table.Header>
                            <Table.Row>
                              <Table.ColumnHeader>Name</Table.ColumnHeader>
                              <Table.ColumnHeader>Description</Table.ColumnHeader>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {filteredContentNames.map((item, idx) => (
                              <Table.Row key={`${item.content_type}-${idx}`}>
                                <Table.Cell>
                                  <Text fontSize="xs" overflowWrap="anywhere" wordBreak="break-word">{item.name}</Text>
                                </Table.Cell>
                                <Table.Cell>
                                  <Text fontSize="xs" color="fg.muted" overflowWrap="anywhere" wordBreak="break-word">{item.description || '-'}</Text>
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table.Root>
                      </Box>
                    </Stack>
                  )}
                </Dialog.Body>
                <Dialog.Footer>
                  <Button variant="ghost" onClick={() => setShowNamesModal(false)}>Close</Button>
                </Dialog.Footer>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>
    </WorkspaceLayout>
  );
}
